import { Controller, useFormContext } from "react-hook-form";

import {
  Connection,
  InboundWebhookConnectionAuthMethod,
} from "src/api/connectApi/types";
import { FormItem } from "src/base-components/FormItem";
import { SimpleDropDown } from "src/base-components/SimpleDropDown";
import { InboundWebhookApiKeyAuthFieldset } from "src/connections/config/inboundWebhook/InboundWebhookApiKeyAuthFieldset";
import { InboundWebhookBasicAuthFieldset } from "src/connections/config/inboundWebhook/InboundWebhookBasicAuthFieldset";
import { InboundWebhookHMACArvaAuthFieldset } from "src/connections/config/inboundWebhook/InboundWebhookHMACArvaAuthFieldset";
import { InboundWebhookHMACAuthFieldset } from "src/connections/config/inboundWebhook/InboundWebhookHMACAuthFieldset";
import { InboundWebhookHMACPersonaAuthFieldset } from "src/connections/config/inboundWebhook/InboundWebhookHMACPersonaAuthFieldset";
import { InboundWebhookTruliooHMACHandshakeAuthFieldset } from "src/connections/config/inboundWebhook/InboundWebhookTruliooHMACHandshakeAuthFieldset";
import {
  Environment,
  InboundWebhookConnectionConfigInputs,
} from "src/connections/types";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { assertUnreachable } from "src/utils/typeUtils";

type ProviderWebhookAuthTypeElements = {
  key: InboundWebhookConnectionAuthMethod;
  value: string;
};

const baseAuthMethods: ProviderWebhookAuthTypeElements[] = [
  { key: "api_key", value: "API key authentication" },
  { key: "basic", value: "Basic authentication" },
  { key: "hmac", value: "HMAC authentication" },
];

const renderAuthFields = (
  authMethod: InboundWebhookConnectionAuthMethod,
  environment: Environment,
) => {
  switch (authMethod) {
    case "no_auth":
      return <></>;
    case "api_key":
      return <InboundWebhookApiKeyAuthFieldset environment={environment} />;
    case "basic":
      return <InboundWebhookBasicAuthFieldset environment={environment} />;
    case "hmac":
      return <InboundWebhookHMACAuthFieldset environment={environment} />;
    case "hmac_persona":
      return (
        <InboundWebhookHMACPersonaAuthFieldset environment={environment} />
      );
    case "hmac_arva":
      return <InboundWebhookHMACArvaAuthFieldset environment={environment} />;
    case "hmac_trulioo_handshake":
      return (
        <InboundWebhookTruliooHMACHandshakeAuthFieldset
          environment={environment}
        />
      );
    default:
      assertUnreachable(authMethod);
  }
};

const hasAuthMethodConfigured = (
  connection: Connection | undefined,
  environment: Environment,
  auth_method: InboundWebhookConnectionAuthMethod,
) => {
  if (environment === "production") {
    return (
      connection?.configuration.webhook_auth_settings?.auth_method ===
      auth_method
    );
  }

  return environment === "sandbox"
    ? connection?.non_prod_env_configs?.sandbox?.webhook_auth_settings
        ?.auth_method === auth_method
    : false;
};

type EnvironmentConfigProps = {
  environment: Environment;
  labelsPrefix: string;
  connection?: Connection | undefined;
};

export const InboundWebhookConnectionEnvironmentConfig: React.FC<
  EnvironmentConfigProps
> = ({ environment, labelsPrefix, connection }) => {
  const { watch, control } =
    useFormContext<InboundWebhookConnectionConfigInputs>();
  const envPrefix = `${environment}Config` as const;
  const webhookAuthMethod = watch(`${envPrefix}.webhookAuthMethod`);

  const authMethods = [...baseAuthMethods];
  if (
    isFeatureFlagEnabled(FEATURE_FLAGS.webhookNoAuthSetting) ||
    hasAuthMethodConfigured(connection, environment, "no_auth")
  ) {
    authMethods.unshift({ key: "no_auth", value: "No authentication" });
  }
  if (
    isFeatureFlagEnabled(FEATURE_FLAGS.webhookHmacPersonaAuthSetting) ||
    hasAuthMethodConfigured(connection, environment, "hmac_persona")
  ) {
    authMethods.push({
      key: "hmac_persona",
      value: "HMAC (Persona) authentication",
    });
  }
  if (
    isFeatureFlagEnabled(FEATURE_FLAGS.webhookHmacArvaAuthSetting) ||
    hasAuthMethodConfigured(connection, environment, "hmac_arva")
  ) {
    authMethods.push({
      key: "hmac_arva",
      value: "HMAC (Arva) authentication",
    });
  }
  if (
    isFeatureFlagEnabled(FEATURE_FLAGS.webhookHmacTruliooHandshake) ||
    hasAuthMethodConfigured(connection, environment, "hmac_trulioo_handshake")
  ) {
    authMethods.push({
      key: "hmac_trulioo_handshake",
      value: "HMAC (Trulioo) authentication",
    });
  }
  return (
    <>
      <FormItem
        key={`${envPrefix}.authMethod`}
        description="The authentication mechanism used by the webhook provider"
        gap="sm"
        label={
          labelsPrefix
            ? `Select ${labelsPrefix.toLowerCase()} authentication type`
            : "Select authentication type"
        }
        isRequired
      >
        <Controller
          control={control}
          name={`${envPrefix}.webhookAuthMethod`}
          render={(props) => (
            <SimpleDropDown
              buttonClassName="pl-3"
              buttonDataLoc={`${environment}-webhook-connection-auth-type`}
              className="h-8 w-full"
              elements={authMethods}
              itemsWidth="w-full"
              placeholder="Select authentication type"
              placement="bottomLeft"
              selectedKey={String(props.field.value)}
              onSelect={(value) => {
                props.field.onChange(value);
              }}
            />
          )}
        ></Controller>
      </FormItem>
      {renderAuthFields(webhookAuthMethod, environment)}
    </>
  );
};
